import Queue from './queue';
import throttle from './throttle';
import NDJSON from './ndjson';
import { truncateModel, METADATA_MODEL } from './truncate';
import { ERRORS, HTTP_REQUEST_TIMEOUT, QUEUE_FLUSH, TRANSACTIONS } from './constants';
import { noop } from './utils';
import { Promise } from './polyfills';
import { compressMetadata, compressTransaction, compressError, compressPayload } from './compress';
import { __DEV__ } from '../state';
import { sendFetchRequest, shouldUseFetchWithKeepAlive } from './http/fetch';
import { sendXHR } from './http/xhr';
var THROTTLE_INTERVAL = 60000;
var ApmServer = function () {
  function ApmServer(configService, loggingService) {
    this._configService = configService;
    this._loggingService = loggingService;
    this.queue = undefined;
    this.throttleEvents = noop;
  }
  var _proto = ApmServer.prototype;
  _proto.init = function init() {
    var _this = this;
    var queueLimit = this._configService.get('queueLimit');
    var flushInterval = this._configService.get('flushInterval');
    var limit = this._configService.get('eventsLimit');
    var onFlush = function onFlush(events) {
      var promise = _this.sendEvents(events);
      if (promise) {
        promise.catch(function (reason) {
          _this._loggingService.warn('Failed sending events!', _this._constructError(reason));
        });
      }
    };
    this.queue = new Queue(onFlush, {
      queueLimit: queueLimit,
      flushInterval: flushInterval
    });
    this.throttleEvents = throttle(this.queue.add.bind(this.queue), function () {
      return _this._loggingService.warn('Dropped events due to throttling!');
    }, {
      limit: limit,
      interval: THROTTLE_INTERVAL
    });
    this._configService.observeEvent(QUEUE_FLUSH, function () {
      _this.queue.flush();
    });
  };
  _proto._postJson = function _postJson(endPoint, payload) {
    var _this2 = this;
    var headers = {
      'Content-Type': 'application/x-ndjson'
    };
    var apmRequest = this._configService.get('apmRequest');
    var params = {
      payload: payload,
      headers: headers,
      beforeSend: apmRequest
    };
    return compressPayload(params).catch(function (error) {
      if (__DEV__) {
        _this2._loggingService.debug('Compressing the payload using CompressionStream API failed', error.message);
      }
      return params;
    }).then(function (result) {
      return _this2._makeHttpRequest('POST', endPoint, result);
    }).then(function (_ref) {
      var responseText = _ref.responseText;
      return responseText;
    });
  };
  _proto._constructError = function _constructError(reason) {
    var url = reason.url,
      status = reason.status,
      responseText = reason.responseText;
    if (typeof status == 'undefined') {
      return reason;
    }
    var message = url + ' HTTP status: ' + status;
    if (__DEV__ && responseText) {
      try {
        var serverErrors = [];
        var response = JSON.parse(responseText);
        if (response.errors && response.errors.length > 0) {
          response.errors.forEach(function (err) {
            return serverErrors.push(err.message);
          });
          message += ' ' + serverErrors.join(',');
        }
      } catch (e) {
        this._loggingService.debug('Error parsing response from APM server', e);
      }
    }
    return new Error(message);
  };
  _proto._makeHttpRequest = function _makeHttpRequest(method, url, _temp) {
    var _ref2 = _temp === void 0 ? {} : _temp,
      _ref2$timeout = _ref2.timeout,
      timeout = _ref2$timeout === void 0 ? HTTP_REQUEST_TIMEOUT : _ref2$timeout,
      payload = _ref2.payload,
      headers = _ref2.headers,
      beforeSend = _ref2.beforeSend;
    var sendCredentials = this._configService.get('sendCredentials');
    if (!beforeSend && shouldUseFetchWithKeepAlive(method, payload)) {
      return sendFetchRequest(method, url, {
        keepalive: true,
        timeout: timeout,
        payload: payload,
        headers: headers,
        sendCredentials: sendCredentials
      }).catch(function (reason) {
        if (reason instanceof TypeError) {
          return sendXHR(method, url, {
            timeout: timeout,
            payload: payload,
            headers: headers,
            beforeSend: beforeSend,
            sendCredentials: sendCredentials
          });
        }
        throw reason;
      });
    }
    return sendXHR(method, url, {
      timeout: timeout,
      payload: payload,
      headers: headers,
      beforeSend: beforeSend,
      sendCredentials: sendCredentials
    });
  };
  _proto.fetchConfig = function fetchConfig(serviceName, environment) {
    var _this3 = this;
    var _this$getEndpoints = this.getEndpoints(),
      configEndpoint = _this$getEndpoints.configEndpoint;
    if (!serviceName) {
      return Promise.reject('serviceName is required for fetching central config.');
    }
    configEndpoint += "?service.name=" + serviceName;
    if (environment) {
      configEndpoint += "&service.environment=" + environment;
    }
    var localConfig = this._configService.getLocalConfig();
    if (localConfig) {
      configEndpoint += "&ifnonematch=" + localConfig.etag;
    }
    var apmRequest = this._configService.get('apmRequest');
    return this._makeHttpRequest('GET', configEndpoint, {
      timeout: 5000,
      beforeSend: apmRequest
    }).then(function (xhr) {
      var status = xhr.status,
        responseText = xhr.responseText;
      if (status === 304) {
        return localConfig;
      } else {
        var remoteConfig = JSON.parse(responseText);
        var etag = xhr.getResponseHeader('etag');
        if (etag) {
          remoteConfig.etag = etag.replace(/["]/g, '');
          _this3._configService.setLocalConfig(remoteConfig, true);
        }
        return remoteConfig;
      }
    }).catch(function (reason) {
      var error = _this3._constructError(reason);
      return Promise.reject(error);
    });
  };
  _proto.createMetaData = function createMetaData() {
    var cfg = this._configService;
    var metadata = {
      service: {
        name: cfg.get('serviceName'),
        version: cfg.get('serviceVersion'),
        agent: {
          name: 'rum-js',
          version: cfg.version
        },
        language: {
          name: 'javascript'
        },
        environment: cfg.get('environment')
      },
      labels: cfg.get('context.tags')
    };
    return truncateModel(METADATA_MODEL, metadata);
  };
  _proto.addError = function addError(error) {
    var _this$throttleEvents;
    this.throttleEvents((_this$throttleEvents = {}, _this$throttleEvents[ERRORS] = error, _this$throttleEvents));
  };
  _proto.addTransaction = function addTransaction(transaction) {
    var _this$throttleEvents2;
    this.throttleEvents((_this$throttleEvents2 = {}, _this$throttleEvents2[TRANSACTIONS] = transaction, _this$throttleEvents2));
  };
  _proto.ndjsonErrors = function ndjsonErrors(errors, compress) {
    var key = compress ? 'e' : 'error';
    return errors.map(function (error) {
      var _NDJSON$stringify;
      return NDJSON.stringify((_NDJSON$stringify = {}, _NDJSON$stringify[key] = compress ? compressError(error) : error, _NDJSON$stringify));
    });
  };
  _proto.ndjsonMetricsets = function ndjsonMetricsets(metricsets) {
    return metricsets.map(function (metricset) {
      return NDJSON.stringify({
        metricset: metricset
      });
    }).join('');
  };
  _proto.ndjsonTransactions = function ndjsonTransactions(transactions, compress) {
    var _this4 = this;
    var key = compress ? 'x' : 'transaction';
    return transactions.map(function (tr) {
      var _NDJSON$stringify2;
      var spans = '',
        breakdowns = '';
      if (!compress) {
        if (tr.spans) {
          spans = tr.spans.map(function (span) {
            return NDJSON.stringify({
              span: span
            });
          }).join('');
          delete tr.spans;
        }
        if (tr.breakdown) {
          breakdowns = _this4.ndjsonMetricsets(tr.breakdown);
          delete tr.breakdown;
        }
      }
      return NDJSON.stringify((_NDJSON$stringify2 = {}, _NDJSON$stringify2[key] = compress ? compressTransaction(tr) : tr, _NDJSON$stringify2)) + spans + breakdowns;
    });
  };
  _proto.sendEvents = function sendEvents(events) {
    var _payload, _NDJSON$stringify3;
    if (events.length === 0) {
      return;
    }
    var transactions = [];
    var errors = [];
    for (var i = 0; i < events.length; i++) {
      var event = events[i];
      if (event[TRANSACTIONS]) {
        transactions.push(event[TRANSACTIONS]);
      }
      if (event[ERRORS]) {
        errors.push(event[ERRORS]);
      }
    }
    if (transactions.length === 0 && errors.length === 0) {
      return;
    }
    var cfg = this._configService;
    var payload = (_payload = {}, _payload[TRANSACTIONS] = transactions, _payload[ERRORS] = errors, _payload);
    var filteredPayload = cfg.applyFilters(payload);
    if (!filteredPayload) {
      this._loggingService.warn('Dropped payload due to filtering!');
      return;
    }
    var apiVersion = cfg.get('apiVersion');
    var compress = apiVersion > 2;
    var ndjson = [];
    var metadata = this.createMetaData();
    var metadataKey = compress ? 'm' : 'metadata';
    ndjson.push(NDJSON.stringify((_NDJSON$stringify3 = {}, _NDJSON$stringify3[metadataKey] = compress ? compressMetadata(metadata) : metadata, _NDJSON$stringify3)));
    ndjson = ndjson.concat(this.ndjsonErrors(filteredPayload[ERRORS], compress), this.ndjsonTransactions(filteredPayload[TRANSACTIONS], compress));
    var ndjsonPayload = ndjson.join('');
    var _this$getEndpoints2 = this.getEndpoints(),
      intakeEndpoint = _this$getEndpoints2.intakeEndpoint;
    return this._postJson(intakeEndpoint, ndjsonPayload);
  };
  _proto.getEndpoints = function getEndpoints() {
    var serverUrl = this._configService.get('serverUrl');
    var apiVersion = this._configService.get('apiVersion');
    var serverUrlPrefix = this._configService.get('serverUrlPrefix') || "/intake/v" + apiVersion + "/rum/events";
    var intakeEndpoint = serverUrl + serverUrlPrefix;
    var configEndpoint = serverUrl + "/config/v1/rum/agents";
    return {
      intakeEndpoint: intakeEndpoint,
      configEndpoint: configEndpoint
    };
  };
  return ApmServer;
}();
export default ApmServer;